import React, { useState, useEffect, useCallback } from 'react';
import { Plus, FileText, Download, RefreshCw, Trash2, GitBranch, ChevronLeft, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

const STATUS_STYLE = {
  draft: { background: '#f1f5f9', color: '#64748b' },
  reviewed: { background: '#fdf6e3', color: '#927320' },
  approved: { background: '#f0f9e8', color: '#4d7c0f' },
};

export default function ImpactReports({ API_URL, getAuthHeader, refreshKey }) {
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [report, setReport] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', template: 'year_end', label: '', start: '', end: '', program_key: '' });
  const [exporting, setExporting] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [rr, tr, pr] = await Promise.all([
        fetch(`${API_URL}/api/impact/reports`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/reports/templates`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/programs`, { headers: getAuthHeader() }),
      ]);
      if (rr.ok) setReports(await rr.json());
      if (tr.ok) setTemplates(await tr.json());
      if (pr.ok) setPrograms(await pr.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const openReport = async (id) => {
    const r = await fetch(`${API_URL}/api/impact/reports/${id}`, { headers: getAuthHeader() });
    if (r.ok) setReport(await r.json());
  };

  const createReport = async () => {
    if (!form.title.trim() || !form.label.trim() || !form.start || !form.end) return toast.error('Title, label and dates are required');
    const r = await fetch(`${API_URL}/api/impact/reports`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, program_key: form.program_key || null })
    });
    if (r.ok) { toast.success('Report created with a live data snapshot'); setShowForm(false); setForm({ title: '', template: 'year_end', label: '', start: '', end: '', program_key: '' }); const d = await r.json(); setReport(d); fetchAll(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Could not create report'); }
  };

  const action = async (path, method = 'POST', body = null, msg = 'Done') => {
    const r = await fetch(`${API_URL}/api/impact/reports/${report.id}${path}`, {
      method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success(msg); if (method === 'DELETE') { setReport(null); } else { setReport(d); } fetchAll(); return d; }
    toast.error(d.detail || 'Action failed');
  };

  const saveNarrative = async () => action('', 'PATCH', { narrative: report.narrative, title: report.title }, 'Narrative saved');

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const r = await fetch(`${API_URL}/api/impact/reports/${report.id}/export?format=${format}`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.title}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error('Export failed'); }
    setExporting('');
  };

  // ---------- detail view ----------
  if (report) {
    const frozen = report.status === 'approved';
    const k = report.data?.kpis || {};
    return (
      <div className="space-y-4" data-testid="impact-report-detail">
        <button onClick={() => { setReport(null); fetchAll(); }} data-testid="impact-report-back"
          className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-700">
          <ChevronLeft className="w-4 h-4" /> All reports
        </button>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded" style={STATUS_STYLE[report.status]}>{report.status}</span>
                <span className="text-[10px] font-bold px-2 py-1 rounded bg-slate-100 text-slate-500">v{report.version}</span>
                <span className="text-[10px] font-bold px-2 py-1 rounded bg-slate-100 text-slate-500">{report.template_name}</span>
                {frozen && <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded" style={{ background: '#f0f9e8', color: '#4d7c0f' }}><Lock className="w-3 h-3" /> Snapshot frozen {String(report.approved_at).slice(0, 10)}</span>}
              </div>
              {frozen ? (
                <h3 className="text-xl font-bold" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>{report.title}</h3>
              ) : (
                <input value={report.title} onChange={e => setReport(r => ({ ...r, title: e.target.value }))}
                  data-testid="impact-report-title-input" className="text-xl font-bold border-b border-dashed border-slate-300 focus:outline-none w-full" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }} />
              )}
              <p className="text-xs text-slate-400 mt-1">{report.label} · {report.start} → {report.end} · {report.data?.filters?.program_name || 'All programs'} · Data generated {String(report.data?.generated_at).slice(0, 16).replace('T', ' ')} UTC</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {['pdf', 'docx', 'xlsx'].map(f => (
                <button key={f} onClick={() => exportFile(f)} disabled={exporting === f} data-testid={`impact-report-export-${f}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
                  style={{ background: BRAND.navy }}>
                  <Download className="w-3.5 h-3.5" /> {exporting === f ? '…' : f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
            {report.status === 'draft' && <>
              <button onClick={() => action('/refresh', 'POST', null, 'Data refreshed from live records')} data-testid="impact-report-refresh"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border border-slate-300 text-slate-600 hover:bg-slate-50"><RefreshCw className="w-3.5 h-3.5" /> Refresh data</button>
              <button onClick={() => action('/status', 'POST', { status: 'reviewed' }, 'Marked as reviewed')} data-testid="impact-report-to-reviewed"
                className="px-3 py-2 rounded-lg text-xs font-bold text-white" style={{ background: BRAND.gold, color: BRAND.navy }}>Mark reviewed</button>
            </>}
            {report.status === 'reviewed' && <>
              <button onClick={() => action('/status', 'POST', { status: 'draft' }, 'Back to draft')} className="px-3 py-2 rounded-lg text-xs font-bold border border-slate-300 text-slate-600">Back to draft</button>
              <button onClick={() => window.confirm('Approve this report? The data snapshot will be frozen and the report can no longer be edited (you can create versioned revisions).') && action('/status', 'POST', { status: 'approved' }, 'Report approved — snapshot frozen')}
                data-testid="impact-report-approve" className="px-3 py-2 rounded-lg text-xs font-bold text-white" style={{ background: '#4d7c0f' }}>Approve & freeze</button>
            </>}
            {frozen && (
              <button onClick={() => action('/revise', 'POST', null, 'New draft revision created with fresh data')} data-testid="impact-report-revise"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white" style={{ background: BRAND.teal }}>
                <GitBranch className="w-3.5 h-3.5" /> Create revision (v{report.version + 1})</button>
            )}
            {!frozen && (
              <button onClick={() => window.confirm('Delete this report?') && action('', 'DELETE', null, 'Report deleted')}
                data-testid="impact-report-delete" className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 ml-auto">
                <Trash2 className="w-3.5 h-3.5" /> Delete</button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[['Unique people', k.unique_people], ['Participations', k.total_participations],
            ['Events delivered', k.events_delivered], ['Orgs engaged', k.orgs_engaged]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <p className="text-2xl font-bold" style={{ color: BRAND.navy }}>{v ?? '—'}</p>
              <p className="text-xs text-slate-500">{l}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Narrative sections</h4>
            {!frozen && <button onClick={saveNarrative} data-testid="impact-report-save-narrative"
              className="px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ background: BRAND.teal }}>Save narrative</button>}
          </div>
          <div className="space-y-4">
            {Object.entries(report.narrative || {}).map(([section, text]) => (
              <div key={section}>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{section}</label>
                <textarea value={text} rows={3} readOnly={frozen}
                  data-testid={`impact-report-narrative-${section.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  onChange={e => setReport(r => ({ ...r, narrative: { ...r.narrative, [section]: e.target.value } }))}
                  className={`w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1 ${frozen ? 'bg-slate-50 text-slate-500' : ''}`}
                  placeholder={frozen ? '' : 'Write this section — it exports into the PDF and DOCX…'} />
              </div>
            ))}
          </div>
        </div>

        {(report.data?.limitations || []).length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-500">
            <p className="font-bold text-slate-600 mb-1.5">Data notes & limitations included in exports</p>
            <ul className="list-disc ml-4 space-y-1">{report.data.limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
          </div>
        )}
      </div>
    );
  }

  // ---------- list view ----------
  return (
    <div className="space-y-4" data-testid="impact-reports">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Build year-end, annual, program, training and board/funder reports from verified dashboard data. Approving a report freezes its numbers forever.</p>
        <button onClick={() => setShowForm(f => !f)} data-testid="impact-add-report-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shrink-0" style={{ background: BRAND.teal }}>
          <Plus className="w-4 h-4" /> New report
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 grid md:grid-cols-2 gap-3" data-testid="impact-report-form">
          <input placeholder="Report title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            data-testid="impact-report-title" className="border border-slate-300 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <select value={form.template} onChange={e => setForm(f => ({ ...f, template: e.target.value }))}
            data-testid="impact-report-template" className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            {templates.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
          <input placeholder="Period label (e.g. FY2026) *" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            data-testid="impact-report-label" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input type="date" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} data-testid="impact-report-start" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input type="date" value={form.end} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} data-testid="impact-report-end" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <select value={form.program_key} onChange={e => setForm(f => ({ ...f, program_key: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white md:col-span-2">
            <option value="">All programs (organization-wide)</option>
            {programs.map(p => <option key={p.key} value={p.key}>{p.name} only</option>)}
          </select>
          <div className="flex gap-2 md:col-span-2">
            <button onClick={createReport} data-testid="impact-report-create" className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.navy }}>Create report</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 border border-slate-200">Cancel</button>
          </div>
        </div>
      )}

      {reports.length === 0 && !showForm ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400 text-sm">
          No reports yet. Create your first Year-End Impact Summary — the data is already waiting.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {reports.map(r => (
            <button key={r.id} onClick={() => openReport(r.id)} data-testid={`impact-report-card-${r.id}`}
              className="bg-white rounded-xl border border-slate-200 p-5 text-left hover:border-teal-300 transition-all">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4" style={{ color: BRAND.teal }} />
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded" style={STATUS_STYLE[r.status]}>{r.status}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-500">v{r.version}</span>
              </div>
              <h3 className="font-bold text-sm" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>{r.title}</h3>
              <p className="text-xs text-slate-400 mt-1">{r.template_name} · {r.label} · {r.start} → {r.end}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
